import { TransactionCategory } from '../types/accounts.types';

/**
 * Maps a reversal category to the original category it undoes.
 * Returns `undefined` for categories that are not reversals.
 */
export const originalCategoryForReversal = (
  category: TransactionCategory,
): TransactionCategory | undefined => {
  switch (category) {
    case TransactionCategory.DEBT_PAYMENT_REVERSAL:
      return TransactionCategory.DEBT_PAYMENT;
    case TransactionCategory.LOAN_PAYMENT_REVERSAL:
      return TransactionCategory.LOAN_PAYMENT;
    case TransactionCategory.LOAN_GIVEN_REVERSAL:
      return TransactionCategory.LOAN_GIVEN;
    case TransactionCategory.EXPENSE_REVERSAL:
      return TransactionCategory.EXPENSE;
    case TransactionCategory.DIVIDEND_REVERSAL:
      return TransactionCategory.DIVIDEND;
    case TransactionCategory.INVESTMENT_REVERSAL:
      return TransactionCategory.INVESTMENT;
    default:
      return undefined;
  }
};

/**
 * Returns the canonical (non-reversal) category for a transaction category.
 * For reversals, this is the original category they undo. For all other
 * categories, it is the category itself.
 *
 * Use this whenever transactions are grouped, filtered, or aggregated by
 * category in the UI — it ensures reversals fold into the same bucket as
 * their counterpart originals.
 */
export const canonicalTransactionCategory = (
  category: TransactionCategory,
): TransactionCategory =>
  originalCategoryForReversal(category) ?? category;

/**
 * The full set of reversal categories.
 */
export const REVERSAL_TRANSACTION_CATEGORIES: ReadonlySet<TransactionCategory> =
  new Set<TransactionCategory>([
    TransactionCategory.DEBT_PAYMENT_REVERSAL,
    TransactionCategory.LOAN_PAYMENT_REVERSAL,
    TransactionCategory.LOAN_GIVEN_REVERSAL,
    TransactionCategory.EXPENSE_REVERSAL,
    TransactionCategory.DIVIDEND_REVERSAL,
    TransactionCategory.INVESTMENT_REVERSAL,
  ]);

export const isReversalCategory = (
  category: TransactionCategory,
): boolean => REVERSAL_TRANSACTION_CATEGORIES.has(category);
